import { useState } from "react";
import type { OrgMember, OrgVenue } from "./api.js";

const ORG_ROLES = ["owner", "admin", "member"] as const;
const VENUE_ROLES = ["manager", "bartender"] as const;

interface Props {
  readonly members: readonly OrgMember[];
  readonly venues: readonly OrgVenue[];
  readonly busy: string | null;
  readonly onInvite: (body: {
    email: string;
    displayName: string;
    orgRole: string;
    venues: { venueId: string; venueRole: string }[];
  }) => Promise<void>;
  readonly onOrgRole: (userId: string, orgRole: string) => Promise<void>;
  readonly onAssign: (userId: string, venueId: string, venueRole: string) => Promise<void>;
  readonly onUnassign: (userId: string, venueId: string) => Promise<void>;
  readonly onRemove: (member: OrgMember) => Promise<void>;
}

interface Draft {
  readonly venueId: string;
  readonly venueRole: string;
}

/**
 * The Members tab: who is in the organisation, what they may do, and where
 * they work.
 *
 * Assignments are chips rather than a nested table — most people work at one
 * venue, and a chip with its own remove control keeps "take Hayden off
 * Collingwood" to a single click without a dialog.
 */
export function Members(p: Props) {
  const [inviting, setInviting] = useState(false);
  const [email, setEmail] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [orgRole, setOrgRole] = useState<string>("member");
  const [drafts, setDrafts] = useState<Draft[]>([]);
  const [adding, setAdding] = useState<string | null>(null);

  const owners = p.members.filter((m) => m.orgRole === "owner").length;

  function resetInvite() {
    setInviting(false);
    setEmail("");
    setDisplayName("");
    setOrgRole("member");
    setDrafts([]);
  }

  const unassigned = (member: OrgMember) =>
    p.venues.filter((v) => !member.venues.some((mv) => mv.venueId === v.venueId));

  return (
    <>
      <div className="org-section">
        <h3>People · {p.members.length}</h3>
        <div className="table">
          <div className="thead">
            <span className="m-name">Name and email</span>
            <span className="m-role">Org role</span>
            <span className="m-venues" style={{ padding: 0 }}>Venues</span>
            <span className="m-act" />
          </div>

          {p.members.map((m) => {
            const lastOwner = m.orgRole === "owner" && owners === 1;
            const free = unassigned(m);
            return (
              <div className="trow org-trow" key={m.userId}>
                <span className="m-name">
                  <span className="n">{m.displayName || m.email || m.userId}</span>
                  <span className="s">
                    {m.email || "no address on file"}
                    {m.isYou ? " · you" : ""}
                  </span>
                </span>

                <span className="m-role">
                  <select
                    value={m.orgRole ?? "member"}
                    disabled={p.busy === m.userId || lastOwner}
                    title={lastOwner ? "The only owner. Make somebody else an owner first." : undefined}
                    onChange={(e) => void p.onOrgRole(m.userId, e.target.value)}
                  >
                    {ORG_ROLES.map((r) => (
                      <option key={r} value={r}>{r}</option>
                    ))}
                  </select>
                </span>

                <span className="m-venues">
                  {m.venues.map((v) => (
                    <span className="chip assign-chip" key={v.venueId}>
                      {v.name}
                      <span className="r">{v.role ?? "no role"}</span>
                      <button
                        aria-label={`Remove ${m.displayName} from ${v.name}`}
                        disabled={p.busy === m.userId}
                        onClick={() => void p.onUnassign(m.userId, v.venueId)}
                      >
                        ×
                      </button>
                    </span>
                  ))}
                  {m.venues.length === 0 && (
                    <span className="label">
                      {m.orgRole === "owner" || m.orgRole === "admin"
                        ? "No shifts — org role only"
                        : "No access until assigned"}
                    </span>
                  )}
                  {free.length > 0 &&
                    (adding === m.userId ? (
                      <AddAssignment
                        venues={free}
                        onCancel={() => setAdding(null)}
                        onAdd={async (venueId, venueRole) => {
                          await p.onAssign(m.userId, venueId, venueRole);
                          setAdding(null);
                        }}
                      />
                    ) : (
                      <button
                        className="btn sm dashed"
                        disabled={p.busy === m.userId}
                        onClick={() => setAdding(m.userId)}
                      >
                        + Venue
                      </button>
                    ))}
                </span>

                <span className="m-act">
                  <button
                    className="btn sm danger"
                    disabled={p.busy === m.userId || lastOwner}
                    title={lastOwner ? "The only owner cannot be removed." : undefined}
                    onClick={() => void p.onRemove(m)}
                  >
                    Remove
                  </button>
                </span>
              </div>
            );
          })}
        </div>
      </div>

      <div className="org-section">
        <h3>Invite</h3>
        {!inviting ? (
          <div>
            <button className="btn dashed" onClick={() => setInviting(true)}>
              + Invite somebody
            </button>
          </div>
        ) : (
          <div className="org-card">
            <div className="org-grid3">
              <label className="field">
                <span>Email</span>
                <input
                  type="email"
                  value={email}
                  placeholder="name@bar.com"
                  onChange={(e) => setEmail(e.target.value)}
                />
              </label>
              <label className="field">
                <span>Name</span>
                <input
                  type="text"
                  value={displayName}
                  placeholder="H. Lambert"
                  onChange={(e) => setDisplayName(e.target.value)}
                />
              </label>
              <label className="field">
                <span>Org role</span>
                <select value={orgRole} onChange={(e) => setOrgRole(e.target.value)}>
                  {ORG_ROLES.map((r) => (
                    <option key={r} value={r}>{r}</option>
                  ))}
                </select>
              </label>
            </div>

            <div className="org-section" style={{ gap: 8 }}>
              <h3>Venues</h3>
              {drafts.map((d, i) => (
                <div className="assign-row" key={`${d.venueId}-${i}`}>
                  <select
                    value={d.venueId}
                    onChange={(e) =>
                      setDrafts((list) =>
                        list.map((x, j) => (j === i ? { ...x, venueId: e.target.value } : x)),
                      )
                    }
                  >
                    {p.venues.map((v) => (
                      <option key={v.venueId} value={v.venueId}>{v.name}</option>
                    ))}
                  </select>
                  <select
                    value={d.venueRole}
                    onChange={(e) =>
                      setDrafts((list) =>
                        list.map((x, j) => (j === i ? { ...x, venueRole: e.target.value } : x)),
                      )
                    }
                  >
                    {VENUE_ROLES.map((r) => (
                      <option key={r} value={r}>{r}</option>
                    ))}
                  </select>
                  <button
                    className="btn sm danger"
                    style={{ padding: "0 8px" }}
                    aria-label="Remove this venue"
                    onClick={() => setDrafts((list) => list.filter((_, j) => j !== i))}
                  >
                    ×
                  </button>
                </div>
              ))}
              {p.venues.length === 0 ? (
                <div className="org-note">No venues yet — add one on the Venues tab.</div>
              ) : (
                <div>
                  <button
                    className="btn sm dashed"
                    onClick={() =>
                      setDrafts((list) => [
                        ...list,
                        { venueId: p.venues[0]!.venueId, venueRole: "bartender" },
                      ])
                    }
                  >
                    + Assign a venue
                  </button>
                </div>
              )}
            </div>

            <div className="org-note">
              Cognito emails them a temporary password. They set their own on first sign-in —
              nothing here ever handles one.
            </div>

            <div className="row">
              <span className="spacer" />
              <button className="btn" onClick={resetInvite} disabled={p.busy === "invite"}>
                Cancel
              </button>
              <button
                className="btn primary"
                disabled={p.busy === "invite" || !email.trim()}
                onClick={async () => {
                  await p.onInvite({
                    email: email.trim(),
                    displayName: displayName.trim(),
                    orgRole,
                    venues: drafts.map((d) => ({ venueId: d.venueId, venueRole: d.venueRole })),
                  });
                  resetInvite();
                }}
              >
                {p.busy === "invite" ? "Inviting…" : "Send the invitation"}
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

function AddAssignment(props: {
  readonly venues: readonly OrgVenue[];
  readonly onAdd: (venueId: string, venueRole: string) => Promise<void>;
  readonly onCancel: () => void;
}) {
  const [venueId, setVenueId] = useState(props.venues[0]!.venueId);
  const [venueRole, setVenueRole] = useState<string>("bartender");
  return (
    <span className="row" style={{ gap: 6 }}>
      <select value={venueId} onChange={(e) => setVenueId(e.target.value)} style={{ height: 36 }}>
        {props.venues.map((v) => (
          <option key={v.venueId} value={v.venueId}>{v.name}</option>
        ))}
      </select>
      <select value={venueRole} onChange={(e) => setVenueRole(e.target.value)} style={{ height: 36 }}>
        {VENUE_ROLES.map((r) => (
          <option key={r} value={r}>{r}</option>
        ))}
      </select>
      <button className="btn sm" onClick={() => void props.onAdd(venueId, venueRole)}>Add</button>
      <button className="btn sm" style={{ color: "var(--t2)" }} onClick={props.onCancel}>Cancel</button>
    </span>
  );
}
